import {
  getRegistrationData,
  updateRegistrationData,
} from "./registrationSetupStorage";
import { getStoredPaymentTypeFields } from "./registrationPaymentType";
import {
  getRegistrationStepNameFromPath,
  trackRegistrationStepBack,
} from "../../helper/registrationAnalytics";

/** Set when user opens insurance details from the coverage-found edit flow. */
export const INSURANCE_DETAILS_RETURN_TO_COVERAGE_KEY =
  "insuranceDetailsReturnToCoverage";

export const markInsuranceDetailsReturnToCoverage = () => {
  updateRegistrationData({ [INSURANCE_DETAILS_RETURN_TO_COVERAGE_KEY]: true });
};

export const clearInsuranceDetailsReturnToCoverage = () => {
  const data = getRegistrationData();
  if (!data[INSURANCE_DETAILS_RETURN_TO_COVERAGE_KEY]) {
    return;
  }
  updateRegistrationData({ [INSURANCE_DETAILS_RETURN_TO_COVERAGE_KEY]: false });
};

const shouldReturnInsuranceDetailsToCoverage = (registrationData = {}) =>
  Boolean(registrationData[INSURANCE_DETAILS_RETURN_TO_COVERAGE_KEY]);

/** Set when coverage is reached after the initial "Find My Coverage" verification flow. */
export const COVERAGE_RETURN_TO_INSURANCE_KEY =
  "coverageReturnToInsuranceVerification";

export const markCoverageReturnToInsuranceVerification = () => {
  updateRegistrationData({ [COVERAGE_RETURN_TO_INSURANCE_KEY]: true });
};

export const clearCoverageReturnToInsuranceVerification = () => {
  const data = getRegistrationData();
  if (!data[COVERAGE_RETURN_TO_INSURANCE_KEY]) {
    return;
  }
  updateRegistrationData({ [COVERAGE_RETURN_TO_INSURANCE_KEY]: false });
};

const shouldReturnCoverageToInsurance = (registrationData = {}) =>
  Boolean(registrationData[COVERAGE_RETURN_TO_INSURANCE_KEY]);

const isOnInsuranceCoveragePath = (path = "") =>
  path.includes("/insurance/coverage");

export const REGISTRATION_STEP_ROUTES = {
  WELCOME: "/register",
  ACCOUNT: "/register/account",
  PAYMENT: "/register/account/payment",
  INSURANCE: "/register/account/insurance",
  INSURANCE_COVERAGE: "/register/account/insurance/coverage",
  CARD: "/register/account/card",
  DIETITIANS: "/register/account/dietitians",
};

/** Location state key: restore password fields when returning to account via step back. */
export const RESTORE_ACCOUNT_PASSWORD_STATE_KEY = "restoreAccountPassword";

const navigateRegistrationRoute = (
  history,
  route,
  { replace = true, state } = {}
) => {
  if (!history) {
    return;
  }
  if (replace && typeof history.replace === "function") {
    history.replace(route, state);
    return;
  }
  if (typeof history.push === "function") {
    history.push(route, state);
  }
};

const normalizePath = (pathname = "") =>
  String(pathname).replace(/\/$/, "") || "";

const isInsurancePaymentType = (registrationData = {}) =>
  getStoredPaymentTypeFields(registrationData).paymentType === "insurance";

/** Previous registration step route for the current path (step 3 → 2 → 1, etc.). */
export const getRegistrationStepBackRoute = (
  pathname = "",
  registrationData = getRegistrationData()
) => {
  const path = normalizePath(pathname);
  const insurance = isInsurancePaymentType(registrationData);

  if (isOnInsuranceCoveragePath(path)) {
    return REGISTRATION_STEP_ROUTES.INSURANCE;
  }
  if (path.endsWith("/insurance/checking")) {
    return REGISTRATION_STEP_ROUTES.INSURANCE;
  }
  if (path.endsWith("/insurance/details")) {
    if (shouldReturnInsuranceDetailsToCoverage(registrationData)) {
      return REGISTRATION_STEP_ROUTES.INSURANCE_COVERAGE;
    }
    return REGISTRATION_STEP_ROUTES.INSURANCE;
  }
  if (path.endsWith("/insurance")) {
    return REGISTRATION_STEP_ROUTES.PAYMENT;
  }
  if (path.endsWith("/payment")) {
    return REGISTRATION_STEP_ROUTES.ACCOUNT;
  }
  if (path.endsWith("/card")) {
    if (insurance || registrationData.coverageResult) {
      return REGISTRATION_STEP_ROUTES.INSURANCE_COVERAGE;
    }
    return REGISTRATION_STEP_ROUTES.PAYMENT;
  }
  if (/\/dietitians\/[^/]+$/.test(path)) {
    return REGISTRATION_STEP_ROUTES.DIETITIANS;
  }
  if (path.endsWith("/dietitians")) {
    return REGISTRATION_STEP_ROUTES.CARD;
  }

  return null;
};

/** Navigate to the previous registration step (not browser history). */
export const registrationStepBack = (history, { replace = true } = {}) => {
  const pathname =
    history?.location?.pathname ?? window.location.pathname ?? "";
  const fromStep = getRegistrationStepNameFromPath(pathname);
  if (fromStep) {
    trackRegistrationStepBack(fromStep);
  }

  const path = normalizePath(pathname);
  const registrationData = getRegistrationData();

  if (
    isOnInsuranceCoveragePath(path) &&
    shouldReturnCoverageToInsurance(registrationData) &&
    history
  ) {
    clearCoverageReturnToInsuranceVerification();
    if (replace && typeof history.replace === "function") {
      history.replace(REGISTRATION_STEP_ROUTES.INSURANCE);
      return;
    }
    if (typeof history.push === "function") {
      history.push(REGISTRATION_STEP_ROUTES.INSURANCE);
      return;
    }
  }

  const backRoute = getRegistrationStepBackRoute(pathname, registrationData);

  if (backRoute && history) {
    if (
      normalizePath(pathname).endsWith("/insurance/details") &&
      shouldReturnInsuranceDetailsToCoverage(registrationData) &&
      backRoute === REGISTRATION_STEP_ROUTES.INSURANCE_COVERAGE
    ) {
      clearInsuranceDetailsReturnToCoverage();
    }

    const navigationState =
      backRoute === REGISTRATION_STEP_ROUTES.ACCOUNT
        ? { [RESTORE_ACCOUNT_PASSWORD_STATE_KEY]: true }
        : undefined;

    navigateRegistrationRoute(history, backRoute, {
      replace,
      state: navigationState,
    });
    return;
  }

  registrationGoBack(history);
};

/** Use browser history stack when no explicit step route applies. */
export const registrationGoBack = (history) => {
  const pathname =
    history?.location?.pathname ?? window.location.pathname ?? "";
  const fromStep = getRegistrationStepNameFromPath(pathname);
  if (fromStep) {
    trackRegistrationStepBack(fromStep);
  }

  if (history && typeof history.go === "function") {
    history.go(-1);
    return;
  }
  if (history && typeof history.push === "function") {
    history.push(REGISTRATION_STEP_ROUTES.ACCOUNT);
  }
};

/** Push or replace within the registration flow. */
export const registrationNavigate = (
  history,
  path,
  { replace = false } = {}
) => {
  if (!history) {
    return;
  }
  if (replace) {
    history.replace(path);
  } else {
    history.push(path);
  }
};
